import React, { useState } from 'react';
import { AlertCircle, Sparkles, Loader2, Brain, X } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { useSubscription } from '../contexts/SubscriptionContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AISessionCreator({ onSessionGenerated, onClose }) {
  const { isPremium } = useSubscription();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  // Natural language input
  const [naturalInput, setNaturalInput] = useState('');

  // Structured form input
  const [structuredInput, setStructuredInput] = useState({
    goal: 'focus',
    duration: 30,
    intensity: 'medium'
  });

  const [examplePrompts, setExamplePrompts] = useState(null);

  // Load example prompts
  React.useEffect(() => {
    const loadExamples = async () => {
      try {
        const response = await axios.get(`${API}/ai-session/examples`);
        setExamplePrompts(response.data.examples);
      } catch (err) {
        console.error('Failed to load example prompts:', err);
      }
    };
    loadExamples();
  }, []);

  const handleGenerateSession = async (inputType) => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      let userInput = '';
      
      if (inputType === 'natural') {
        if (!naturalInput.trim()) {
          setError('Please enter a description of the session you want');
          setLoading(false);
          return;
        }
        userInput = naturalInput;
      } else {
        // Structured input - convert to natural language
        userInput = `Create a ${structuredInput.duration}-minute ${structuredInput.intensity} intensity session for ${structuredInput.goal}`;
      }

      const response = await axios.post(`${API}/ai-session/create`, {
        user_input: userInput,
        input_type: inputType,
      }, { withCredentials: true });

      if (response.data.success) {
        setResult(response.data.result);
      } else {
        setError('Failed to generate session. Please try again.');
      }
    } catch (err) {
      console.error('AI session generation error:', err);
      if (err.response?.status === 401) {
        setError('Please sign in to use the AI Session Creator.');
      } else if (err.response?.status === 403) {
        setError('AI Session Creator is a premium feature. Please upgrade to access.');
      } else {
        setError(err.response?.data?.detail || 'Failed to generate session. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUseSession = () => {
    if (result && result.custom_session) {
      onSessionGenerated(result.custom_session);
      onClose();
    }
  };

  const handleRegenerate = () => {
    setResult(null);
  };

  const insertExamplePrompt = (prompt) => {
    setNaturalInput(prompt);
  };

  if (!isPremium) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-8 max-w-md w-full border border-purple-500/30 shadow-2xl">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-purple-600 rounded-lg">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-white">AI Session Creator</h2>
          </div>
          <p className="text-gray-300 mb-6">
            AI Session Creator is a premium feature. Unlock it to create custom brainwave sessions powered by advanced AI.
          </p>
          <button
            onClick={onClose}
            className="w-full px-4 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-6 max-w-4xl w-full border border-purple-500/30 shadow-2xl my-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-600 rounded-lg">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">AI Session Creator</h2>
              <p className="text-sm text-gray-400">Powered by advanced AI</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Results Display */}
        {result && (
          <div className="mb-6 p-4 bg-gradient-to-r from-purple-900/30 to-blue-900/30 rounded-lg border border-purple-500/30">
            <h3 className="text-lg font-bold text-white mb-2">
              {result.session_type === 'preset_recommendation' ? 'Recommended Presets' : 'Custom Session Generated'}
            </h3>
            
            {result.session_type === 'preset_recommendation' && result.recommended_presets && (
              <div className="mb-4">
                <p className="text-gray-300 mb-3">Based on your input, try these existing presets:</p>
                <div className="space-y-2">
                  {result.recommended_presets.map((preset, idx) => (
                    <div key={idx} className="p-3 bg-gray-800/50 rounded-lg">
                      <p className="text-white font-medium">{preset}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {result.session_type === 'custom' && result.custom_session && (
              <div className="space-y-3">
                <div>
                  <h4 className="text-white font-bold text-xl mb-1">{result.custom_session.name}</h4>
                  <p className="text-gray-300">{result.custom_session.description}</p>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="p-3 bg-gray-800/50 rounded-lg">
                    <p className="text-xs text-gray-400 mb-1">Duration</p>
                    <p className="text-white font-bold">{Math.round(result.custom_session.duration / 60)} min</p>
                  </div>
                  <div className="p-3 bg-gray-800/50 rounded-lg">
                    <p className="text-xs text-gray-400 mb-1">Beat Type</p>
                    <p className="text-white font-bold capitalize">{result.custom_session.beat_type}</p>
                  </div>
                  <div className="p-3 bg-gray-800/50 rounded-lg">
                    <p className="text-xs text-gray-400 mb-1">Base Frequency</p>
                    <p className="text-white font-bold">{result.custom_session.base_frequency} Hz</p>
                  </div>
                  <div className="p-3 bg-gray-800/50 rounded-lg">
                    <p className="text-xs text-gray-400 mb-1">Timeline Nodes</p>
                    <p className="text-white font-bold">{result.custom_session.timeline_nodes?.length || 0}</p>
                  </div>
                </div>

                {result.custom_session.background_audio_recommendation && (
                  <div className="p-3 bg-gray-800/50 rounded-lg">
                    <p className="text-xs text-gray-400 mb-1">Recommended Background Audio</p>
                    <p className="text-white">{result.custom_session.background_audio_recommendation}</p>
                  </div>
                )}
              </div>
            )}

            <div className="mt-4 p-3 bg-gray-800/30 rounded-lg">
              <p className="text-xs text-gray-400 mb-1">AI Explanation</p>
              <p className="text-gray-300 text-sm">{result.explanation}</p>
            </div>

            <div className="mt-4 flex gap-3">
              {result.session_type === 'custom' && result.custom_session && (
                <button
                  onClick={handleUseSession}
                  className="flex-1 px-4 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors font-medium flex items-center justify-center gap-2"
                >
                  <Brain className="w-5 h-5" />
                  Use This Session
                </button>
              )}
              <button
                onClick={handleRegenerate}
                className="px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        )}

        {/* Input Interface */}
        {!result && (
          <>
            <Tabs defaultValue="natural" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="natural">Natural Language</TabsTrigger>
                <TabsTrigger value="structured">Structured Form</TabsTrigger>
              </TabsList>

              {/* Natural Language Tab */}
              <TabsContent value="natural" className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Describe the session you want
                  </label>
                  <textarea
                    value={naturalInput}
                    onChange={(e) => setNaturalInput(e.target.value)}
                    placeholder="Example: I need to focus deeply for 90 minutes of coding work..."
                    className="w-full h-32 px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 resize-none"
                  />
                </div>

                {/* Example Prompts */}
                {examplePrompts && (
                  <div>
                    <p className="text-sm font-medium text-gray-300 mb-3">Example Prompts:</p>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {examplePrompts.map((category, idx) => (
                        <div key={idx}>
                          <p className="text-xs text-gray-500 font-semibold mb-2">{category.category}</p>
                          {category.prompts.map((prompt, pidx) => (
                            <button
                              key={pidx}
                              onClick={() => insertExamplePrompt(prompt)}
                              className="w-full text-left px-3 py-2 bg-gray-800/50 hover:bg-gray-700 text-sm text-gray-300 rounded-lg transition-colors mb-1"
                            >
                              {prompt}
                            </button>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <button
                  onClick={() => handleGenerateSession('natural')}
                  disabled={loading || !naturalInput.trim()}
                  className="w-full px-4 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      Generate Session
                    </>
                  )}
                </button>
              </TabsContent>

              {/* Structured Form Tab */}
              <TabsContent value="structured" className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Goal
                  </label>
                  <select
                    value={structuredInput.goal}
                    onChange={(e) => setStructuredInput({ ...structuredInput, goal: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                  >
                    <option value="focus">Focus & Concentration</option>
                    <option value="relaxation">Relaxation</option>
                    <option value="sleep">Sleep & Deep Rest</option>
                    <option value="meditation">Meditation & Mindfulness</option>
                    <option value="creativity">Creativity & Flow</option>
                    <option value="energy">Energy & Alertness</option>
                    <option value="study">Study & Learning</option>
                    <option value="healing">Healing & Recovery</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Duration: {structuredInput.duration} minutes
                  </label>
                  <input
                    type="range"
                    min="20"
                    max="120"
                    step="5"
                    value={structuredInput.duration}
                    onChange={(e) => setStructuredInput({ ...structuredInput, duration: parseInt(e.target.value) })}
                    className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider-thumb"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>20 min</span>
                    <span>120 min</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Intensity
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    {['light', 'medium', 'deep'].map((intensity) => (
                      <button
                        key={intensity}
                        onClick={() => setStructuredInput({ ...structuredInput, intensity })}
                        className={`px-4 py-3 rounded-lg transition-all font-medium capitalize ${
                          structuredInput.intensity === intensity
                            ? 'bg-purple-600 text-white'
                            : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                        }`}
                      >
                        {intensity}
                      </button>
                    ))}
                  </div>
                </div>

                <button
                  onClick={() => handleGenerateSession('structured')}
                  disabled={loading}
                  className="w-full px-4 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      Generate Session
                    </>
                  )}
                </button>
              </TabsContent>
            </Tabs>
          </>
        )}

        {/* Error Display */}
        {error && (
          <div className="mt-4 p-4 bg-red-900/30 border border-red-500/50 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-300 font-medium">Error</p>
              <p className="text-red-200 text-sm">{error}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
